import jwtDecode from 'jwt-decode';
import fp from 'lodash/fp';
import queryString from 'query-string';
import React, { Component } from 'react';
import { Button, Col, Grid, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import { LinkContainer } from 'react-router-bootstrap';
import { push } from 'react-router-redux';
import { bindActionCreators } from 'redux';
import { links, routes } from '../../../constants';
import { handleInvalidForm, validateEmail, validatePassword } from '../../../services';
import { userActions } from '../../../state/modules/user';
import { Auth0Error, ControlGroup, ControlOption, InputFormField, LoadingButton, PasswordFormField } from '../../components';

// Financial Adviser industry role ID is 2.
// This role is pre-inserted to backend database.
const DefaultIndustryRoleId = 2

class InvitationSignUp extends Component {

  constructor() {
    super();

    this.state = {
      invitationSignUpPayload: {
        pendingUser: {
          firstName: '',
          lastName: '',
          email: '',
          password: ''
        },
        businessId: null,
        industryRoleId: null,
        invitationToken: null
      },
      validation: {
        firstName: true,
        lastName: true,
        email: true,
        password: true,
        businessName: true,
        readTerms: true,
      },
      submitted: false,
      isSignUpBusinessPlan: false
    };
  }

  componentDidMount() {
    const invitationToken = queryString.parse(this.props.location.search).invitationToken;
    // If we have invalid token provided, boot User to login page.
    try {
      const { firstName, lastName, email, businessId } = jwtDecode(invitationToken);
      const nextPendingUser = Object.assign({}, this.state.invitationSignUpPayload.pendingUser, {
        firstName: firstName || '',
        lastName: lastName || '',
        email: email || ''
      });
      const nextPayload = Object.assign({}, this.state.invitationSignUpPayload, {
        pendingUser: nextPendingUser,
        businessId: parseInt(businessId, 10)
      });
      this.setState({
        invitationSignUpPayload: nextPayload
      });
    }
    catch (e) {
      this.props.goTo(routes.login);
    }
  }

  onPendingUserChange(event) {
    let nextPayload = Object.assign({}, this.state.invitationSignUpPayload);
    nextPayload.pendingUser[event.target.name] = event.target.value;
    this.setState({ invitationSignUpPayload: nextPayload });
  }

  signUpInvitation(e) {
    e.preventDefault();
    e.stopPropagation();

    // Allow validation messages (if any) to show.
    this.setState({ submitted: true });

    if (this.validateForm()) {
      const invitationToken = queryString.parse(this.props.location.search).invitationToken;

      this.props.invitationSignUp(
        Object.assign({}, this.state.invitationSignUpPayload, { invitationToken: invitationToken, industryRoleId: DefaultIndustryRoleId })
      );
    }
    handleInvalidForm(e.target);
  }

  /**
   * Validates the sign up form and sets the validation state.
   * 
   * Returns true if all fields are valid, false otherwise.
   */
  validateForm() {
    const validation = {
      firstName: this.state.invitationSignUpPayload.pendingUser.firstName.trim() !== '',
      lastName: this.state.invitationSignUpPayload.pendingUser.lastName.trim() !== '',
      email: validateEmail(this.state.invitationSignUpPayload.pendingUser.email),
      password: validatePassword(this.state.invitationSignUpPayload.pendingUser.password),
      readTerms: this.state.readTerms
    };

    this.setState({ validation: validation });

    return fp.all(fp.identity)(validation);
  }

  isReadTerms = () => !this.state.submitted || (this.state.submitted && this.state.validation.readTerms);

  render() {

    const firstNameProps = {
      id: 'first-name',
      validation: this.state.validation.firstName ? null : 'First name is required.',
      value: this.state.invitationSignUpPayload.pendingUser.firstName,
      required: true,
      onChange: (e) => this.onPendingUserChange(e),
      inputProps: {
        maxLength: 255
      }
    }

    const lastNameProps = {
      id: 'last-name',
      validation: this.state.validation.lastName ? null : 'Last name is required.',
      value: this.state.invitationSignUpPayload.pendingUser.lastName,
      required: true,
      onChange: (e) => this.onPendingUserChange(e),
      inputProps: {
        maxLength: 255
      }
    }

    const emailProps = {
      id: 'email',
      validation: this.state.validation.email ?
        null :
        this.state.invitationSignUpPayload.pendingUser.email ?
          'Email is required.' :
          'Must be a valid email address.',
      value: this.state.invitationSignUpPayload.pendingUser.email,
      required: true,
      onChange: (e) => this.onPendingUserChange(e),
      inputProps: {
        maxLength: 255
      }
    }

    const passwordProps = {
      id: 'password',
      validation: this.state.validation.password ? null : 'Password is required.',
      value: this.state.invitationSignUpPayload.pendingUser.password,
      required: true,
      onChange: (e) => this.onPendingUserChange(e)
    }

    const readTermsProps = {
      name: 'readTerms',
      label: <span>I've read and accept the Caddie <a href={links.thinkCaddie.terms} target='_blank'>terms of service</a> and <a href={links.thinkCaddie.privacy} target='_blank'>privacy policy</a>.</span>,
      'aria-describedby': this.isReadTerms() ? 'term-validation' : null,
      'aria-invalid': this.isReadTerms()
    };

    const termsProps = {
      id: 'terms',
      control: 'checkbox',
      label: null,
      required: true,
      onChange: (e) => this.setState({ [e.target.name]: e.target.checked })
    }

    return (
      <section id='content--signup'>
        <Grid>
          <h1 className="text-center">Welcome to Caddie.</h1>
          <Row className='no-gutter'>
            <Col xs={12} sm={6} smOffset={3}>
              {this.props.user ? <Auth0Error user={this.props.user} location={this.props.location} /> : ''}
              <form noValidate onSubmit={(e) => this.signUpInvitation(e)}>
                <h2 className='center-sm'>First, let's get to know you.</h2>
                <fieldset>
                  <InputFormField {...firstNameProps} />
                  <InputFormField {...lastNameProps} />
                  <InputFormField {...emailProps} />
                  <PasswordFormField {...passwordProps} />
                  <ControlGroup {...termsProps}>
                    <ControlOption {...readTermsProps} />
                    {
                      !this.isReadTerms() ?
                        <div aria-invalid='true' className='has-error'>
                          <span id='term-validation' className='help-block' role="alert">You must accept the terms of service and privacy policy.</span>
                        </div> :
                        null
                    }
                  </ControlGroup>
                  <LoadingButton text='Continue' type='submit' loading={this.props.user.isFetching} className='full large' />
                  <LinkContainer className='full large' exact to={{ pathname: routes.app.confirmAcceptInvitation, search: this.props.location.search }}>
                    <Button bsStyle='plain'>I have an account</Button>
                  </LinkContainer>
                </fieldset>
              </form>
            </Col>
          </Row>
        </Grid>
      </section>
    );
  }
}

const mapStateToProps = (state) => ({
  user: state.user
});

const mapDispatchToProps = (dispatch) => ({
  goTo: bindActionCreators(push, dispatch),
  invitationSignUp: bindActionCreators(userActions.user.invitationSignUp, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(InvitationSignUp);



// WEBPACK FOOTER //
// ./src/containers/pages/onboarding/invitation-signup.js